import React, { useCallback, useContext, useState, useEffect, forwardRef, useMemo, useRef } from "react";
import DatePicker from "react-datepicker";
import { changeReportScheduleActive, createReport, deleteReportSchedule, getReportSchedules, scheduleReport } from "./reportService";
import { listThemes } from "./theme/themeService";
import { showDangerToast, showSuccessToast, showWarningToast } from "../validation/ToastContainer";
import { PopupContext, TableContext } from "../index";
import { getDeletePopup } from "../view/popup/PopupContainer";
import ChooseDeviceField from "./ChooseDeviceField";
import SelectV2 from "../view/SelectV2";
import { EMAIL_REGEX } from "../vars";
import { connect } from "react-redux";
import { X, CalendarDays, Info, Mail } from "lucide-react";
import { Switch } from "@mui/material";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import SearchIcon from "@mui/icons-material/Search";

const WEEK_DAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const YEAR_MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

function getDefaultScheduleSelections() {
    const currentDate = new Date();

    return {
        weekDay: WEEK_DAYS[currentDate.getDay()],
        dayOfMonth: String(currentDate.getDate()),
        monthName: YEAR_MONTHS[currentDate.getMonth()]
    };
}

function getDefaultScheduleTime() {
    const currentDate = new Date();
    currentDate.setMinutes(Math.ceil((currentDate.getMinutes() + 1) / 5) * 5);
    currentDate.setSeconds(0);
    currentDate.setMilliseconds(0);

    const hours24 = currentDate.getHours();
    const hour12 = hours24 % 12 || 12;

    return {
        hour: String(hour12),
        minute: String(currentDate.getMinutes()),
        period: hours24 >= 12 ? "PM" : "AM"
    };
}

function CreateReportModal({ handleClose, pk, userRole, username }) {

    const { config, setConfig } = useContext(TableContext);
    const { setPopup } = useContext(PopupContext);
    const defaultScheduleSelections = useMemo(() => getDefaultScheduleSelections(), []);
    const defaultScheduleTime = useMemo(() => getDefaultScheduleTime(), []);
    const [dateFrom, setDateFrom] = useState(new Date());
    const [dateTo, setDateTo] = useState(new Date());
    const [viewSchedules, setViewSchedules] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const [reportScheduleResult, setReportScheduleResult] = useState([]);

    useEffect(() => {
        const originalBodyOverflow = document.body.style.overflow;
        const originalHtmlOverflow = document.documentElement.style.overflow;
        document.body.style.overflow = "hidden";
        document.documentElement.style.overflow = "hidden";

        return () => {
            document.body.style.overflow = originalBodyOverflow;
            document.documentElement.style.overflow = originalHtmlOverflow;
        };
    }, []);

    const DatePickerInput = forwardRef(({ value, onClick, placeholder }, ref) => (
        <button
            type="button"
            className={`create-report-date-input ${value ? 'has-value' : ''}`}
            onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onClick();
            }}
            ref={ref}
        >
            <span className="create-report-date-input__text">{value || placeholder}</span>
            <CalendarDays size={18} className="create-report-date-input__icon" />
        </button>
    ));

    // const DateToPickerInput = forwardRef(({ value, onClick }, ref) => (
    //     <button
    //         type="button"
    //         className="btn oct_btnreport-datefrom"
    //         onClick={(e) => {
    //             e.preventDefault();
    //             e.stopPropagation(); // critical!
    //             onClick();
    //         }}
    //         ref={ref}
    //     >
    //         {value}
    //     </button>
    // ));

    const reportTypes = [
        { value: 'EXECUTIVE', label: 'Executive' },
        { value: 'DETAILED', label: 'Detailed' }
    ]

    const reportRanges = [
        { value: 'LAST_HOUR', label: 'Last hour' },
        { value: 'LAST_DAY', label: 'Last day' },
        { value: 'LAST_WEEK', label: 'Last week' },
        { value: 'LAST_MONTH', label: 'Last month' },
        { value: 'CUSTOM', label: 'Custom' },
    ]

    // Theme state
    const [userThemes, setUserThemes] = useState([]);

    // Fetch user themes on mount
    useEffect(() => {
        const fetchThemes = async () => {
            try {
                const response = await listThemes();
                if (response.ok) {
                    const themes = await response.json();
                    setUserThemes(themes);
                }
            } catch (err) {
                console.error('Error fetching themes:', err);
            }
        };
        fetchThemes();
    }, []);

    // Combined report types (built-in + user themes)
    const allReportTypes = [
        ...reportTypes,
        ...userThemes.map(t => ({ value: `THEME_${t.id}`, label: `${t.name}` }))
    ];
    const reportTypeOptions = allReportTypes.map((reportType) => ({ value: reportType.value, label: reportType.label }));
    const reportRangeOptions = reportRanges.map((reportRange) => ({ value: reportRange.value, label: reportRange.label }));

    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [type, setType] = useState(reportTypes[0].value);
    // const [range, setRange] = useState(reportRanges[0].value);
    const [range, setRange] = useState(reportRanges[0].value); // Always use custom date range
    const [deviceId, setDeviceId] = useState();
    const [isGenerating, setIsGenerating] = useState(false);
    const [showErrors, setShowErrors] = useState(false);
    const [scheduleEnabled, setScheduleEnabled] = useState(false);
    const [hours, setHours] = useState(defaultScheduleTime.hour);
    const [minutes, setMinutes] = useState(defaultScheduleTime.minute);
    const [period, setPeriod] = useState(defaultScheduleTime.period);
    const [selectedRecurrenceFrequency, setSelectedRecurrenceFrequency] = useState(1);
    const [selectedPattern, setSelectedPattern] = useState("day");
    const [selectedDays, setSelectedDays] = useState(() => [defaultScheduleSelections.weekDay]);
    const [selectedMonthOption, setSelectedMonthOption] = useState(() => defaultScheduleSelections.dayOfMonth);
    const [selectedYearOption, setSelectedYearOption] = useState(() => defaultScheduleSelections.dayOfMonth);
    const [selectedYearNameOption, setSelectedYearNameOption] = useState(() => defaultScheduleSelections.monthName);
    const [sendEmail, setSendEmail] = useState(false);
    const [emailTo, setEmailTo] = useState("");
    const emailFieldRef = useRef(null);
    const emailInputRef = useRef(null);

    useEffect(() => {
        if (!sendEmail) {
            return;
        }

        const animationFrame = window.requestAnimationFrame(() => {
            emailFieldRef.current?.scrollIntoView({
                behavior: "smooth",
                block: "nearest"
            });
            emailInputRef.current?.focus({ preventScroll: true });
        });

        return () => window.cancelAnimationFrame(animationFrame);
    }, [sendEmail]);

    const hourOptions = useMemo(() => (
        Array.from({ length: 12 }, (_, index) => ({
            value: String(index + 1),
            label: `${index + 1} hr.`
        }))
    ), []);
    const minuteOptions = useMemo(() => (
        Array.from({ length: 12 }, (_, index) => {
            const minute = String(index * 5);
            return { value: minute, label: `${minute} min.` };
        })
    ), []);
    const periodOptions = useMemo(() => ([
        { value: "AM", label: "AM" },
        { value: "PM", label: "PM" }
    ]), []);
    const patternOptions = useMemo(() => ([
        { value: "day", label: "day" },
        { value: "week", label: "week" },
        { value: "month", label: "month" },
        { value: "year", label: "year" }
    ]), []);
    const dayOfMonthOptions = useMemo(() => (
        Array.from({ length: 31 }, (_, index) => {
            const day = String(index + 1);
            return { value: day, label: day };
        })
    ), []);
    const yearMonthOptions = useMemo(() => (
        YEAR_MONTHS.map((month) => ({ value: month, label: month }))
    ), []);

    const emailList = emailTo
        .split(",")
        .map((email) => email.trim())
        .filter(Boolean);
    const emailsAreValid = !sendEmail || (emailList.length > 0 && emailList.every((email) => EMAIL_REGEX.test(email)));
    const scheduleTimeReady = Boolean(hours) && Boolean(minutes) && Boolean(period);
    const recurrenceReady = selectedPattern !== "week" || selectedDays.length > 0;

    const reportDetailsReady = name
        && description
        && type
        && range
        && (range !== 'CUSTOM' || (dateFrom && dateTo));
    const scheduleDetailsReady = scheduleTimeReady
        && recurrenceReady
        && emailsAreValid;
    const valid = reportDetailsReady && (!scheduleEnabled || scheduleDetailsReady);

    const getReportPayload = () => {
        let finalType = type;
        let finalThemeId = null;

        if (type.startsWith('THEME_')) {
            finalType = 'CUSTOM_THEME';
            finalThemeId = parseInt(type.split('_')[1]);
        }

        return {
            name,
            description,
            type: finalType,
            themeId: finalThemeId,
            deviceId,
            range,
            dateFrom,
            dateTo,
            mimeType: 'PDF'
        };
    };

    // Date validation handlers
    // const handleDateFromChange = (date) => {
    //     setDateFrom(date);
    //     // If dateFrom is after dateTo, update dateTo to match dateFrom
    //     if (date > dateTo) {
    //         setDateTo(date);
    //     }
    // };

    // const handleDateToChange = (date) => {
    //     // Only set dateTo if it's not before dateFrom
    //     if (date >= dateFrom) {
    //         setDateTo(date);
    //     } else {
    //         // If user tries to set dateTo before dateFrom, set it to dateFrom
    //         setDateTo(dateFrom);
    //     }
    // };
    // const handleCreateClick = () => {
    //     handleCreate(); 
    //     if (range === 'CUSTOM') {
    //         handleCreateCustom();
    //     }
    //     else {
    //         handleCreateRange();
    //     }
    // }
    const getWeeklyDaysForSubmit = useCallback(() => {
        if (selectedDays.length > 0) {
            return selectedDays.join(",");
        }

        return defaultScheduleSelections.weekDay;
    }, [defaultScheduleSelections.weekDay, selectedDays]);

    const getScheduleRecurrenceSummary = useCallback(({
        recurrencePattern = selectedPattern,
        recurEvery = selectedRecurrenceFrequency,
        selectedWeekDays = selectedDays.join(","),
        selectedMonthDay = selectedMonthOption,
        selectedYearMonth = selectedYearNameOption,
        selectedYearDay = selectedYearOption,
        useScheduleLabel = true
    } = {}) => {
        if (recurrencePattern === "day") {
            return `${useScheduleLabel ? "The Report will be generated every" : "Generate every"} ${recurEvery === 1 ? "day" : `${recurEvery} days`}`;
        }

        if (recurrencePattern === "week") {
            return `${useScheduleLabel ? "The Report will be generated every" : "Generate every"} ${recurEvery === 1 ? "week" : `${recurEvery} weeks`} on ${selectedWeekDays || getWeeklyDaysForSubmit()}`;
        }

        if (recurrencePattern === "month") {
            return `${useScheduleLabel ? "The Report will be generated on day" : "Generate on day"} ${selectedMonthDay} for every ${recurEvery === 1 ? "month" : `${recurEvery} months`}`;
        }

        return `${useScheduleLabel ? "The Report will be generated every" : "Generate every"} ${selectedYearMonth} ${selectedYearDay}`;
    }, [
        getWeeklyDaysForSubmit,
        selectedDays,
        selectedMonthOption,
        selectedPattern,
        selectedRecurrenceFrequency,
        selectedYearNameOption,
        selectedYearOption
    ]);

    const fetchSchedules = useCallback(async () => {
        const result = await getReportSchedules(userRole);
        const schedules = Array.isArray(result)
            ? result
            : Object.values(result || {}).filter((item) => item && typeof item === "object");
        setReportScheduleResult(schedules);
    }, [userRole]);

    useEffect(() => {
        if (!viewSchedules) {
            return;
        }

        fetchSchedules();
        const intervalId = window.setInterval(fetchSchedules, 5000);

        return () => window.clearInterval(intervalId);
    }, [fetchSchedules, viewSchedules]);

    const getRecurrenceSummary = () => {
        if (selectedPattern === "day") {
            return `The Report will be generated every ${selectedRecurrenceFrequency === 1 ? "day" : `${selectedRecurrenceFrequency} days`}`;
        }

        if (selectedPattern === "week") {
            const selectedWeekDays = selectedDays.length > 0 ? selectedDays.join(", ") : "no days selected";
            return `The Report will be generated every ${selectedRecurrenceFrequency === 1 ? "week" : `${selectedRecurrenceFrequency} weeks`} on ${selectedWeekDays}`;
        }

        if (selectedPattern === "month") {
            return `The Report will be generated on day ${selectedMonthOption} for every ${selectedRecurrenceFrequency === 1 ? "month" : `${selectedRecurrenceFrequency} months`}`;
        }

        return `The Report will be generated every ${selectedYearNameOption} ${selectedYearOption}`;
    };

    const toggleWeekDay = (day) => {
        setSelectedDays((currentDays) => {
            if (currentDays.includes(day)) {
                return currentDays.filter((selectedDay) => selectedDay !== day);
            }

            return WEEK_DAYS.filter((weekDay) => [...currentDays, day].includes(weekDay));
        });
    };

    const getDateTimeFormat = (dateValue) => {
        if (!dateValue) {
            return "";
        }

        const date = new Date(dateValue);
        const yyyy = date.getFullYear();
        const MM = String(date.getMonth() + 1).padStart(2, "0");
        const dd = String(date.getDate()).padStart(2, "0");
        const minuteValue = String(date.getMinutes()).padStart(2, "0");
        const hh = String(date.getHours() % 12 || 12).padStart(2, "0");
        const ampm = date.getHours() >= 12 ? "PM" : "AM";

        return `${yyyy}-${MM}-${dd} ${hh}:${minuteValue} ${ampm}`;
    };

    const schedules = useMemo(() => (
        reportScheduleResult.map((schedule, index) => ({
            ...schedule,
            key: schedule.id ?? `report-schedule-${index}`
        }))
    ), [reportScheduleResult]);

    const filteredSchedules = useMemo(() => {
        const query = searchQuery.trim().toLowerCase();

        if (!query) {
            return schedules;
        }

        return schedules.filter((schedule) => {
            const haystack = [
                schedule.name,
                schedule.description,
                schedule.emailTo,
                schedule.type,
                schedule.range,
                schedule.recurrencePattern,
                getScheduleRecurrenceSummary({
                    recurrencePattern: schedule.recurrencePattern,
                    recurEvery: schedule.recurEvery,
                    selectedWeekDays: schedule.selectedWeekDays,
                    selectedMonthDay: schedule.selectedMonthDay,
                    selectedYearMonth: schedule.selectedYearMonth,
                    selectedYearDay: schedule.selectedYearDay,
                    useScheduleLabel: false
                }),
                getDateTimeFormat(schedule.lastScheduledTime)
            ].join(" ").toLowerCase();

            return haystack.includes(query);
        });
    }, [getScheduleRecurrenceSummary, schedules, searchQuery]);

    const handleViewSchedules = () => {
        setSearchQuery("");
        setViewSchedules(true);
    };

    const handleToggleChange = (scheduleId, currentActive) => {
        const nextActive = !currentActive;

        setReportScheduleResult((currentSchedules) => currentSchedules.map((schedule) => (
            String(schedule.id) === String(scheduleId)
                ? { ...schedule, active: nextActive }
                : schedule
        )));

        changeReportScheduleActive(scheduleId, nextActive)
            .then(() => showSuccessToast("Report Schedule is Updated", "Report Schedule"));
    };

    const handleDelete = async (id) => {
        deleteReportSchedule(id)
            .then(async () => {
                showSuccessToast("Report Schedule is Deleted", "Delete Schedule");
                await fetchSchedules();
            })
            .catch((error) => {
                console.error("Error deleting report schedule:", error);
            });
    };

    const handleClickDeleteOpen = (id) => {
        setPopup(
            getDeletePopup(
                "Are you sure you want to delete this schedule?",
                "Delete Schedule",
                () => handleDelete(id),
                undefined,
                "Delete Schedule"
            )
        );
    };

    const handleCreateClick = () => {
        if (isGenerating) return; // Prevent double submission

        if (!valid) {
            setShowErrors(true);
            if (scheduleEnabled && selectedPattern === "week" && selectedDays.length === 0) {
                showWarningToast("Please select at least one day for the weekly schedule.", "Report Schedule");
                return;
            }
            if (scheduleEnabled && sendEmail && !emailsAreValid) {
                showWarningToast("Please enter valid comma-separated email addresses before creating the schedule.", "Report Schedule");
            }
            return;
        }

        const reportDto = getReportPayload();

        if (!scheduleEnabled) {
            setIsGenerating(true);

            createReport(reportDto).then(async (response) => {
                if (response?.ok) {
                    showSuccessToast(`${name} report created`, "Create Report");
                    setConfig({ ...config, lastUpdated: Date.now() });
                    handleClose();
                }
                else {
                    const json = response ? await response.json() : { message: "No response from server" };
                    showDangerToast(`Something went wrong: ${json.message}`, `Create report: ${response?.status || "Error"} `);
                }
            }).finally(() => {
                setIsGenerating(false);
            });
            return;
        }

        const hourNumber = parseInt(hours, 10);
        const normalizedHour = period === "AM"
            ? (hourNumber === 12 ? 0 : hourNumber)
            : (hourNumber === 12 ? 12 : hourNumber + 12);
        const startTimeDb = new Date();

        startTimeDb.setHours(normalizedHour);
        startTimeDb.setMinutes(parseInt(minutes, 10));
        startTimeDb.setSeconds(0);
        startTimeDb.setMilliseconds(0);

        const reportSchedule = {
            ...reportDto,
            userId: pk,
            userRole,
            username,
            emailTo: sendEmail ? emailTo.trim() : null,
            startTime: startTimeDb.toISOString(),
            recurrencePattern: selectedPattern,
            recurEvery: Number(selectedRecurrenceFrequency),
            selectedWeekDays: selectedPattern === "week" ? getWeeklyDaysForSubmit() : null,
            selectedMonthDay: selectedPattern === "month" ? Number(selectedMonthOption) : null,
            selectedYearMonth: selectedPattern === "year" ? selectedYearNameOption : null,
            selectedYearDay: selectedPattern === "year" ? Number(selectedYearOption) : null,
            active: true
        };

        setIsGenerating(true);

        scheduleReport(reportSchedule).then(async (response) => {
            if (response?.ok) {
                const createdSchedule = await response.json();
                showSuccessToast(`${name} report schedule created`, "Report Schedule");
                setConfig({ ...config, lastUpdated: Date.now() });
                setReportScheduleResult((currentSchedules) => [
                    createdSchedule,
                    ...currentSchedules.filter((schedule) => String(schedule.id) !== String(createdSchedule.id))
                ]);
                setSearchQuery("");
                setViewSchedules(true);
                fetchSchedules();
            }
            else {
                const json = response ? await response.json() : { message: "No response from server" };
                showDangerToast(`Something went wrong: ${json.message}`, `Report Schedule: ${response?.status || "Error"} `)
            }
        }).finally(() => {
            setIsGenerating(false);
        })
    }

    // const handleCreateCustom = () => {
    //     const reportDto = {
    //         name,
    //         description,
    //         type,
    //         range,
    //         selectedDateFrom,
    //         selectedDateTo,
    //         mimeType: 'PDF' //todo change
    //     }

    //     //handleCreate();      

    //     createReportCustom(reportDto).then(async (response) => {
    //         if(response.ok) {
    //             showSuccessToast(`${name} report created`, "Create report");
    //             setConfig({...config, lastUpdated: Date.now()});
    //         }
    //         else {
    //             const json = await response.json();
    //             showDangerToast(`Something went wrong: ${json.message}`, `Create report: ${response.status} `)
    //         }
    //     })
    // }


    const content = (
        <div className="modal oct_display-block modal-v2-root">
            <div className="modal oct_modal modal-v2-overlay create-report-modal-overlay">
                <div className="modal-dialog modal-lg modal-dialog-centered modal-v2-dialog create-report-modal-dialog" aria-modal="true">
                    <div className="modal-content modal-v2-shell create-report-modal">
                        <div className="modal-v2-header create-report-modal-header">
                            <div className="modal-v2-header-top">
                                <div className="modal-v2-header-copy">
                                    <h2 className="modal-v2-heading create-report-modal-title">{viewSchedules ? "Report Schedule List" : "Create Report"}</h2>
                                    <p className="modal-v2-subheading create-report-modal-subtitle">{viewSchedules ? "Manage scheduled reports" : "Schedule a comprehensive security report"}</p>
                                </div>
                            </div>
                            <button type="button" className="modal-v2-close create-report-modal-close" aria-label="Close" onClick={() => handleClose()}>
                                <X size={20} />
                            </button>
                        </div>
                        <div className="modal-body modal-v2-body create-report-modal-body">
                            {viewSchedules ? (
                                <>
                                    <div className="modal-v2-schedule-list-header">
                                        <div className="modal-v2-schedule-list-search">
                                            <SearchIcon fontSize="small" />
                                            <input
                                                type="text"
                                                value={searchQuery}
                                                onChange={(event) => setSearchQuery(event.target.value)}
                                                placeholder="Search schedules..."
                                            />
                                        </div>
                                    </div>

                                    <div className="modal-v2-schedule-list-table-wrap">
                                        {filteredSchedules.length === 0 ? (
                                            <div className="modal-v2-schedule-empty-state">No schedules found.</div>
                                        ) : (
                                            <table className="modal-v2-schedule-list-table">
                                                <thead>
                                                    <tr className="modal-v2-schedule-list-header-row">
                                                        <th className="modal-v2-schedule-list-col-start-time">Start Time</th>
                                                        <th className="modal-v2-schedule-list-col-report">Report</th>
                                                        <th className="modal-v2-schedule-list-col-email">Email</th>
                                                        <th className="modal-v2-schedule-list-col-type">Type</th>
                                                        <th className="modal-v2-schedule-list-col-date-range">Date Range</th>
                                                        <th className="modal-v2-schedule-list-col-pattern">Pattern</th>
                                                        <th className="modal-v2-schedule-list-col-recurrence">Recurrence</th>
                                                        <th className="modal-v2-schedule-list-col-last-execution">Last Execution</th>
                                                        <th className="modal-v2-schedule-list-col-enable">Enable</th>
                                                        <th className="modal-v2-schedule-list-col-delete">Delete</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {filteredSchedules.map((schedule) => (
                                                        <tr key={schedule.key}>
                                                            <td>{new Date(schedule.startTime).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}</td>
                                                            <td className="modal-v2-schedule-list-cell-report">{schedule.name}</td>
                                                            <td className="modal-v2-schedule-list-cell-email">{schedule.emailTo || "-"}</td>
                                                            <td className="modal-v2-schedule-list-cell-type">{schedule.type}</td>
                                                            <td className="modal-v2-schedule-list-cell-date-range">{schedule.range}</td>
                                                            <td>{schedule.recurrencePattern}</td>
                                                            <td>
                                                                {getScheduleRecurrenceSummary({
                                                                    recurrencePattern: schedule.recurrencePattern,
                                                                    recurEvery: schedule.recurEvery,
                                                                    selectedWeekDays: schedule.selectedWeekDays,
                                                                    selectedMonthDay: schedule.selectedMonthDay,
                                                                    selectedYearMonth: schedule.selectedYearMonth,
                                                                    selectedYearDay: schedule.selectedYearDay,
                                                                    useScheduleLabel: false
                                                                })}
                                                            </td>
                                                            <td>{getDateTimeFormat(schedule.lastScheduledTime) || "-"}</td>
                                                            <td className="modal-v2-schedule-list-cell-center">
                                                                <Switch
                                                                    checked={Boolean(schedule.active)}
                                                                    onChange={() => handleToggleChange(schedule.id, schedule.active)}
                                                                    disabled={!schedule.id}
                                                                />
                                                            </td>
                                                            <td className="modal-v2-schedule-list-cell-center">
                                                                <button
                                                                    type="button"
                                                                    className="modal-v2-schedule-table-delete"
                                                                    aria-label="Delete Schedule"
                                                                    title="Delete Schedule"
                                                                    onClick={() => handleClickDeleteOpen(schedule.id)}
                                                                    disabled={!schedule.id}
                                                                >
                                                                    <DeleteOutlineOutlinedIcon fontSize="small" />
                                                                </button>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        )}
                                    </div>
                                </>
                            ) : (
                            <form
                                id="create-report-form"
                                className="create-report-form"
                                autoComplete="off"
                                onSubmit={(event) => {
                                    event.preventDefault();
                                    handleCreateClick();
                                }}
                            >
                                <section className="create-report-section">
                                    <div className="create-report-section-header">
                                        <span className="create-report-section-bar" />
                                        <h3 className="create-report-section-title">Report Details</h3>
                                    </div>
                                    <div className="create-report-grid">
                                        <div className="create-report-field">
                                            <label htmlFor="reportName">Report Name <span className="text-danger">*</span></label>
                                            <input
                                                type="text"
                                                id="reportName"
                                                className={`create-report-input ${showErrors && !name ? 'is-invalid' : ''}`}
                                                value={name}
                                                onChange={event => setName(event.target.value)}
                                                placeholder="Enter report name"
                                            />
                                        </div>

                                        <div className="create-report-field">
                                            <label htmlFor="reportDescription">Description <span className="text-danger">*</span></label>
                                            <input
                                                type="text"
                                                id="reportDescription"
                                                className={`create-report-input ${showErrors && !description ? 'is-invalid' : ''}`}
                                                value={description}
                                                onChange={event => setDescription(event.target.value)}
                                                placeholder="Enter description"
                                            />
                                        </div>
                                    </div>
                                </section>

                                <section className="create-report-section">
                                    <div className="create-report-section-header">
                                        <span className="create-report-section-bar" />
                                        <h3 className="create-report-section-title">Report Scope</h3>
                                    </div>

                                    <div className="create-report-grid">
                                        <div className="create-report-field">
                                            <label htmlFor="reportType">Report Type <span className="text-danger">*</span></label>
                                            <SelectV2
                                                inputId="reportType"
                                                className="create-report-select"
                                                value={type}
                                                onChange={setType}
                                                options={reportTypeOptions}
                                            />
                                        </div>

                                        <ChooseDeviceField
                                            deviceId={deviceId}
                                            setDeviceId={setDeviceId}
                                            className="create-report-field"
                                            selectClassName="oct_select-v2 create-report-select"
                                            labelClassName="create-report-label"
                                        />

                                        <div className="create-report-field create-report-field-full">
                                            <label htmlFor="reportRange">Date Range <span className="text-danger">*</span></label>
                                            <SelectV2
                                                inputId="reportRange"
                                                className="create-report-select"
                                                value={range}
                                                onChange={setRange}
                                                options={reportRangeOptions}
                                                menuPlacement="top"
                                            />
                                        </div>

                                        {range === 'CUSTOM' && (
                                            <div className="create-report-field create-report-field-full create-report-custom-range">
                                                <div className="create-report-custom-range__grid">
                                                    <div className="create-report-field">
                                                        <label htmlFor="report-date-from">From <span className="text-danger">*</span></label>
                                                        <div className="create-report-datepicker" onClick={(e) => e.stopPropagation()}>
                                                            <DatePicker
                                                                id="report-date-from"
                                                                selected={dateFrom}
                                                                onChange={(date) => {
                                                                    setDateFrom(date);
                                                                    if (dateTo && date > dateTo) {
                                                                        setDateTo(date);
                                                                    }
                                                                }}
                                                                maxDate={new Date()}
                                                                customInput={<DatePickerInput placeholder="dd/mm/yyyy" />}
                                                                dateFormat="dd/MM/yyyy"
                                                                popperClassName="create-report-datepicker-popper"
                                                                portalId="create-report-datepicker-portal"
                                                            />
                                                        </div>
                                                    </div>
                                                    <div className="create-report-field">
                                                        <label htmlFor="report-date-to">To <span className="text-danger">*</span></label>
                                                        <div className="create-report-datepicker" onClick={(e) => e.stopPropagation()}>
                                                            <DatePicker
                                                                id="report-date-to"
                                                                selected={dateTo}
                                                                onChange={(date) => {
                                                                    if (!dateFrom || date >= dateFrom) {
                                                                        setDateTo(date);
                                                                    }
                                                                }}
                                                                minDate={dateFrom}
                                                                maxDate={new Date()}
                                                                customInput={<DatePickerInput placeholder="dd/mm/yyyy" />}
                                                                dateFormat="dd/MM/yyyy"
                                                                popperClassName="create-report-datepicker-popper"
                                                                portalId="create-report-datepicker-portal"
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </section>

                                <section className="create-report-section create-report-schedule-section">
                                    <div className="create-report-section-header">
                                        <span className="create-report-section-bar" />
                                        <h3 className="create-report-section-title">Schedule Report</h3>
                                    </div>

                                    <label className="create-report-schedule-toggle" htmlFor="enable-report-schedule">
                                        <input
                                            checked={scheduleEnabled}
                                            id="enable-report-schedule"
                                            onChange={(event) => {
                                                setScheduleEnabled(event.target.checked);
                                                if (!event.target.checked) {
                                                    setSendEmail(false);
                                                    setEmailTo("");
                                                }
                                            }}
                                            type="checkbox"
                                        />
                                        <span className="create-report-schedule-toggle__copy">
                                            <strong>Schedule this report</strong>
                                            <small>Leave unchecked to create the report instantly.</small>
                                        </span>
                                    </label>

                                    {scheduleEnabled && (
                                        <>
                                            <div className="create-report-schedule-group">
                                                <h4 className="create-report-schedule-caption">Schedule Time</h4>
                                                <div className="modal-v2-schedule-time-row">
                                                    <div className="modal-v2-schedule-time-field">
                                                        <label htmlFor="reportScheduleHour">Hour</label>
                                                        <SelectV2 inputId="reportScheduleHour" className="modal-v2-schedule-pattern-select" value={hours} onChange={setHours} options={hourOptions} />
                                                    </div>
                                                    <div className="modal-v2-schedule-time-field">
                                                        <label htmlFor="reportScheduleMin">Minute</label>
                                                        <SelectV2 inputId="reportScheduleMin" className="modal-v2-schedule-pattern-select" value={minutes} onChange={setMinutes} options={minuteOptions} />
                                                    </div>
                                                    <div className="modal-v2-schedule-time-field">
                                                        <label htmlFor="reportSchedulePeriod">Period</label>
                                                        <SelectV2 inputId="reportSchedulePeriod" className="modal-v2-schedule-pattern-select" value={period} onChange={setPeriod} options={periodOptions} />
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="create-report-schedule-group">
                                                <h4 className="create-report-schedule-caption">Recurrence Pattern</h4>
                                                <div className="modal-v2-schedule-recurrence">
                                                    <div className="modal-v2-schedule-recurrence-controls">
                                                        <div className="modal-v2-schedule-recurrence-control">
                                                            <span className="modal-v2-schedule-inline-label">Recur every</span>
                                                            <div className="modal-v2-schedule-repeat-fields">
                                                                {selectedPattern !== "year" && (
                                                                    <input
                                                                        aria-label="Recur every interval"
                                                                        className="oct_form-control-default oct_textalign-center oct_recurrence-frequency-input-v2"
                                                                        type="number"
                                                                        min="1"
                                                                        value={selectedRecurrenceFrequency}
                                                                        onChange={(event) => setSelectedRecurrenceFrequency(Math.max(1, parseInt(event.target.value || 1, 10)))}
                                                                    />
                                                                )}
                                                                <SelectV2 inputId="reportSchedulePattern" className="modal-v2-schedule-pattern-select" value={selectedPattern} onChange={setSelectedPattern} options={patternOptions} />
                                                            </div>
                                                        </div>

                                                        {selectedPattern === "week" && (
                                                            <div className="modal-v2-schedule-weekday-card">
                                                                <span className="modal-v2-schedule-inline-label">On days</span>
                                                                <div className="modal-v2-schedule-weekday-pills" role="group" aria-label="Weekly schedule days">
                                                                    {WEEK_DAYS.map((day) => {
                                                                        const isSelected = selectedDays.includes(day);

                                                                        return (
                                                                            <button
                                                                                aria-pressed={isSelected}
                                                                                className={`modal-v2-schedule-weekday-pill ${isSelected ? "is-selected" : ""}`}
                                                                                key={day}
                                                                                onClick={() => toggleWeekDay(day)}
                                                                                type="button"
                                                                            >
                                                                                <span className="modal-v2-schedule-weekday-pill-short">{day.slice(0, 3)}</span>
                                                                                <span className="modal-v2-schedule-weekday-pill-full">{day}</span>
                                                                            </button>
                                                                        );
                                                                    })}
                                                                </div>
                                                            </div>
                                                        )}

                                                        {selectedPattern === "month" && (
                                                            <div className="modal-v2-schedule-specific-grid">
                                                                <div className="modal-v2-schedule-specific-field">
                                                                    <label className="modal-v2-schedule-inline-label" htmlFor="report-schedule-month-day">On day</label>
                                                                    <SelectV2 inputId="report-schedule-month-day" className="modal-v2-schedule-inline-select" value={selectedMonthOption} onChange={setSelectedMonthOption} options={dayOfMonthOptions} />
                                                                </div>
                                                            </div>
                                                        )}

                                                        {selectedPattern === "year" && (
                                                            <div className="modal-v2-schedule-specific-grid modal-v2-schedule-specific-grid--year">
                                                                <div className="modal-v2-schedule-specific-field">
                                                                    <label className="modal-v2-schedule-inline-label" htmlFor="report-schedule-year-month">On month</label>
                                                                    <SelectV2 inputId="report-schedule-year-month" className="modal-v2-schedule-inline-select" value={selectedYearNameOption} onChange={setSelectedYearNameOption} options={yearMonthOptions} />
                                                                </div>
                                                                <div className="modal-v2-schedule-specific-field">
                                                                    <label className="modal-v2-schedule-inline-label" htmlFor="report-schedule-year-day">On day</label>
                                                                    <SelectV2 inputId="report-schedule-year-day" className="modal-v2-schedule-inline-select" value={selectedYearOption} onChange={setSelectedYearOption} options={dayOfMonthOptions} />
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>

                                                    <div className="modal-v2-schedule-recurrence-summary">
                                                        <Info size={18} />
                                                        <span>{getRecurrenceSummary()}</span>
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="create-report-schedule-group">
                                                <h4 className="create-report-schedule-caption">Report Delivery</h4>
                                                <label className="create-report-email-toggle" htmlFor="send-report-email">
                                                    <input
                                                        checked={sendEmail}
                                                        id="send-report-email"
                                                        onChange={(event) => {
                                                            setSendEmail(event.target.checked);
                                                            if (!event.target.checked) {
                                                                setEmailTo("");
                                                            }
                                                        }}
                                                        type="checkbox"
                                                    />
                                                    <span className="create-report-email-toggle__box">
                                                        <Mail size={17} />
                                                    </span>
                                                    <span className="create-report-email-toggle__copy">
                                                        <strong>Email this report</strong>
                                                        <small>Optional. Schedule can be created without email delivery.</small>
                                                    </span>
                                                </label>

                                                {sendEmail && (
                                                    <div className="create-report-field create-report-field-full create-report-email-field" ref={emailFieldRef}>
                                                        <label htmlFor="reportScheduleEmail">Email <span className="text-danger">*</span></label>
                                                        <input
                                                            id="reportScheduleEmail"
                                                            ref={emailInputRef}
                                                            className={`create-report-input ${showErrors && !emailsAreValid ? 'is-invalid' : ''}`}
                                                            type="text"
                                                            value={emailTo}
                                                            onChange={(event) => setEmailTo(event.target.value)}
                                                            placeholder="name@example.com, team@example.com"
                                                        />
                                                        <small className="create-report-email-help">Use commas to send to multiple emails.</small>
                                                    </div>
                                                )}
                                            </div>
                                        </>
                                    )}
                                </section>

                            </form>
                            )}
                        </div>
                        {viewSchedules ? (
                            <div className="modal-footer modal-v2-footer">
                                <div className="modal-v2-footer-note"></div>
                                <div className="modal-v2-footer-actions create-report-actions">
                                    <button type="button" className="modal-v2-btn modal-v2-btn-primary create-report-btn create-report-btn-primary" onClick={() => setViewSchedules(false)}>
                                        Back
                                    </button>
                                </div>
                            </div>
                        ) : (
                        <div className="modal-footer modal-v2-footer">
                            <div className="modal-v2-footer-note">
                                <span className="modal-v2-footer-note-star">*</span>
                                Required fields
                            </div>
                            <div className="modal-v2-footer-actions create-report-actions">
                                <button type="button" className="modal-v2-btn modal-v2-btn-outline create-report-btn create-report-btn-secondary" onClick={() => handleClose()}>
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    form="create-report-form"
                                    className={`modal-v2-btn modal-v2-btn-primary create-report-btn create-report-btn-primary${!valid ? ' disabled' : ''}`}
                                    disabled={isGenerating || !valid}
                                >
                                    {isGenerating ? 'Saving...' : (scheduleEnabled ? 'Create Schedule' : 'Create Report')}
                                </button>
                                <button type="button" className="modal-v2-btn modal-v2-btn-primary create-report-btn create-report-btn-primary" onClick={handleViewSchedules}>
                                    View Schedules
                                </button>
                            </div>
                        </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
    return content;
}


function mapStateToProps(state) {
    return {
        pk: state.auth.pk,
        username: state.auth.username,
        userRole: state.auth.userRole.name
    };
}

export default connect(mapStateToProps)(CreateReportModal);
